/**
 * Profile Preview Modal - Trade Loop v2.0
 *
 * Shows a preview of a user's profile exactly as other users would see it.
 * Displays public profile information without edit capabilities.
 *
 * Can display:
 * - The logged-in user's own profile (pass no partner prop)
 * - Any other user's profile (pass partner data)
 */

import { X, User, Phone, Mail, Briefcase, MapPin, Wrench, Building2, Ban, ShieldOff, Calendar, Activity, Star, MoreVertical, Send, MessageCircle, UserMinus, UserPlus, Flag } from "lucide-react";
import ProBadge from "@/components/ProBadge";
import { useAuth } from "@/contexts/AuthContext";
import { formatPhone } from "@/utils/formatPhone";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useBlockUser, useUnblockUser } from "@/api/extensions/useBlockUser";
import { ReportContentModal } from "@/components/ReportContentModal";
import { useDeleteConnections } from "@/api/generated_hooks/generated-connections-hooks";
import { useToast } from "@/contexts/ToastContext";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogPortal,
  DialogOverlay,
} from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { getReferralsByRelationship } from "@/api/extensions/referrals-relationship-extension";
import CompactReferralCard from "@/components/CompactReferralCard";
import { useUserTradesAndPrimary } from "@/hooks/useTrades";

interface ProfilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  partner?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    company?: string;
    trigPrimaryTradeName?: string;
    location?: string;
    bio?: string;
    profileImageUrl?: string;
    trades?: string[];
    marketTypes?: string[];
    serviceAreas?: string[];
    subscriptionPlan?: string;
    isFavorite?: boolean;
    [key: string]: any;
  } | null;
  currentUserOrganizationId?: string | null;
  // Additional props for favorite and menu actions
  onToggleFavorite?: () => void;
  showFavoriteButton?: boolean;
  showMenuButton?: boolean;
  // Action buttons
  onSendReferral?: () => void;
  onSendMessage?: () => void;
  showActionButtons?: boolean;
  // Remove partner callback
  onPartnerRemoved?: () => void;
  // Request a connection with a user who is not a partner yet (footer button)
  onRequestConnection?: () => void;
}

export default function ProfilePreviewModal({
  isOpen,
  onClose,
  partner,
  currentUserOrganizationId,
  onToggleFavorite,
  showFavoriteButton = false,
  showMenuButton = false,
  onSendReferral,
  onSendMessage,
  showActionButtons = false,
  onPartnerRemoved,
  onRequestConnection
}: ProfilePreviewModalProps) {
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  const { user: currentUser } = useAuth();
  const { showSuccess, showError } = useToast();
  const blockUser = useBlockUser();
  const unblockUser = useUnblockUser();
  const deleteConnection = useDeleteConnections();
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [unblockDialogOpen, setUnblockDialogOpen] = useState(false);
  const [viewAllReferralsOpen, setViewAllReferralsOpen] = useState(false);
  const [removePartnerDialogOpen, setRemovePartnerDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  // Calculate connection status early (before hooks that depend on it)
  const isOwnProfile = !partner && !!currentUser;
  const inSameOrganization = currentUserOrganizationId &&
    partner?.organizationId &&
    currentUserOrganizationId === partner.organizationId;
  const isConnected = isOwnProfile ||
    inSameOrganization ||
    (partner && 'status' in partner && (partner.status === 'accepted' || partner.status === 'connected'));

  // Determine if we should fetch metrics (before early return)
  const shouldFetchMetrics = !isOwnProfile && isConnected && !!partner?.id && !!currentUser?.id;

  // Fetch referrals sent (current user to partner)
  // Using custom relationship extension which filters on backend for performance
  const { data: sentReferrals } = useQuery({
    queryKey: ['referrals-by-relationship', 'sent', currentUser?.id, partner?.id],
    queryFn: () => {
      console.log('[ProfilePreviewModal] Calling getReferralsByRelationship for SENT referrals');
      return getReferralsByRelationship({
        senderId: currentUser?.id || '',
        receiverId: partner?.id || ''
      });
    },
    enabled: shouldFetchMetrics && isOpen,
    staleTime: 0,
  });

  // Fetch referrals received (partner to current user)
  // Using custom relationship extension which filters on backend for performance
  const { data: receivedReferrals } = useQuery({
    queryKey: ['referrals-by-relationship', 'received', partner?.id, currentUser?.id],
    queryFn: () => {
      console.log('[ProfilePreviewModal] Calling getReferralsByRelationship for RECEIVED referrals');
      return getReferralsByRelationship({
        senderId: partner?.id || '',
        receiverId: currentUser?.id || ''
      });
    },
    enabled: shouldFetchMetrics && isOpen,
    staleTime: 0,
  });

  // Fetch primary trade name + additional trades for the displayed user. This
  // is the single source of truth for the trade section so every caller
  // (Network, Messages, Referral, Organization, Header, Settings) renders the
  // same data, no matter what trade fields the caller happened to pass in.
  const profileUserId = isOwnProfile ? currentUser?.id : partner?.id;
  const { data: userTradesAndPrimary } = useUserTradesAndPrimary(
    profileUserId,
    isOpen && !!profileUserId
  );
  const userTrades = userTradesAndPrimary?.trades;
  const fetchedPrimaryTradeName = userTradesAndPrimary?.primaryTradeName ?? null;

  // NOW we can do the early return after all hooks are called
  if (!isOpen) return null;

  // Helper function to parse JSON strings to arrays
  const parseJsonArray = (value: any): string[] => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  // Use partner data if provided, otherwise fall back to current user
  let user = partner || currentUser;

  // If partner data exists, parse JSONB fields that come as JSON strings
  if (partner) {
    user = {
      ...partner,
      trades: parseJsonArray(partner.trades),
      marketTypes: parseJsonArray(partner.marketTypes),
      serviceAreas: parseJsonArray(partner.serviceAreas),
      // Map sender/receiver createdAt to createdAt for "Member Since" display
      createdAt: partner.createdAt || partner.senderCreatedAt || partner.receiverCreatedAt,
      // Normalize trig fields to standard field names for backward compatibility
      // Accepts both old API field names (primaryTrade, email, phone) and new trig field names
      email: partner.email || partner.trigUserEmail,
      phone: partner.phone || partner.trigUserPhone,
      trigPrimaryTradeName: partner.trigPrimaryTradeName || partner.primaryTrade,
    };
  }

  // DEBUG: Log the actual user data to console
  console.log('[ProfilePreviewModal] User data:', {
    subscriptionPlan: user?.subscriptionPlan,
    fullUser: user
  });

  // User initials for avatar fallback
  const userInitials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`
    : user?.email?.[0]?.toUpperCase() || "U";

  const userDisplayName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.firstName || user?.lastName || "User";

  // Debug logging (isOwnProfile, inSameOrganization, isConnected already calculated above)
  console.log('[ProfilePreviewModal] Contact visibility check:', {
    isOwnProfile,
    currentUserOrganizationId,
    partnerOrganizationId: partner?.organizationId,
    inSameOrganization,
    isConnected,
    partner,
    userPhone: user?.phone,
    userEmail: user?.email,
    showPhoneToConnections: user?.showPhoneToConnections,
    showEmailToConnections: user?.showEmailToConnections
  });

  // Check if this user is currently blocked
  const isBlocked = partner && partner.status === 'blocked';
  // A request already sent (either direction) and not yet answered
  const isRequestPending = partner?.status === 'pending';

  // Handle block user
  const handleBlockUser = () => {
    setBlockDialogOpen(true);
  };

  const confirmBlockUser = async () => {
    console.log('[ProfilePreviewModal] confirmBlockUser called');
    console.log('[ProfilePreviewModal] partner:', partner);
    console.log('[ProfilePreviewModal] partner?.id:', partner?.id);
    console.log('[ProfilePreviewModal] blockUser.isPending:', blockUser.isPending);

    if (!partner?.id) {
      console.log('[ProfilePreviewModal] No partner ID, exiting');
      return;
    }

    try {
      console.log('[ProfilePreviewModal] Calling blockUser.mutateAsync with:', { user_id: partner.id });
      await blockUser.mutateAsync({ user_id: partner.id });
      console.log('[ProfilePreviewModal] blockUser.mutateAsync completed successfully');
      showSuccess("User Blocked", `${userDisplayName} has been blocked`);
      setBlockDialogOpen(false);
      onClose(); // Close the profile preview modal
    } catch (error: any) {
      console.error('[ProfilePreviewModal] Block error:', error);
      const errorMessage = error.response?.data?.error || error.message || "Failed to block user";
      showError("Block Failed", errorMessage);
    }
  };

  // Handle unblock user
  const handleUnblockUser = () => {
    setUnblockDialogOpen(true);
  };

  const confirmUnblockUser = async () => {
    if (!partner?.id) return;

    try {
      await unblockUser.mutateAsync({ user_id: partner.id });
      showSuccess("User Unblocked", `${userDisplayName} has been unblocked`);
      setUnblockDialogOpen(false);
      onClose(); // Close the profile preview modal
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Failed to unblock user";
      showError("Unblock Failed", errorMessage);
    }
  };

  // Handle remove partner connection
  const handleRemovePartner = () => {
    setRemovePartnerDialogOpen(true);
  };

  const confirmRemovePartner = async () => {
    // Use connectionId if available, otherwise use partner's id field
    const connectionId = partner?.connectionId || partner?.id;

    if (!connectionId) {
      showError("Remove Failed", "Could not find connection ID");
      return;
    }

    try {
      await deleteConnection.mutateAsync(connectionId);
      showSuccess("Partner Removed", `${userDisplayName} has been removed from your network`);
      setRemovePartnerDialogOpen(false);
      onPartnerRemoved?.(); // Notify parent to refresh
      onClose(); // Close the profile preview modal
    } catch (error: any) {
      console.error('[ProfilePreviewModal] Remove partner error:', error);
      const errorMessage = error.response?.data?.error || error.message || "Failed to remove partner";
      showError("Remove Failed", errorMessage);
    }
  };

  // Calculate metrics from referral data (hooks already called at the top)
  const relationshipMetrics = {
    referralsSent: sentReferrals?.length || 0,
    referralsReceived: receivedReferrals?.length || 0,
    referralsCompleted: sentReferrals?.filter(ref =>
      ref.outcome === 'completed' ||
      ref.jobCompletedVerified === true
    ).length || 0,
    revenueGenerated: receivedReferrals?.reduce((total, ref) => {
      return total + (ref.revenue || 0);
    }, 0) || 0
  };

  // Debug: Log the actual referral data
  if (shouldFetchMetrics && isOpen) {
    console.log('[ProfilePreviewModal] Referral Metrics Debug:', {
      currentUserId: currentUser?.id,
      partnerId: partner?.id,
      sentReferralsCount: sentReferrals?.length,
      receivedReferralsCount: receivedReferrals?.length,
      sentReferralsSample: sentReferrals?.slice(0, 2).map(r => ({
        id: r.id,
        senderId: r.senderId,
        receiverId: r.receiverId,
        clientName: r.clientName,
        createdAt: r.createdAt,
        outcome: r.outcome
      })),
      receivedReferralsSample: receivedReferrals?.slice(0, 2).map(r => ({
        id: r.id,
        senderId: r.senderId,
        receiverId: r.receiverId
      }))
    });
  }

  // Radix Dialog handles portal mounting + ESC/overlay close. We rely on
  // portal mount order (not hardcoded z-index) so nested modals — opened
  // from inside this one or from a parent that itself opened on top —
  // stack correctly.
  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogPortal>
          <DialogOverlay className="bg-black/50" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="fixed left-[50%] top-[50%] z-50 w-full max-w-md max-h-[85vh] translate-x-[-50%] translate-y-[-50%] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-5">
            {/* Pro Badge - Top Left Corner */}
            <div className="absolute top-4 left-4 z-10">
              <ProBadge subscriptionPlan={user?.subscriptionPlan} size="md" />
            </div>

            {/* Action Buttons Row (Top Right) */}
            <div className="absolute top-4 right-4 flex items-center space-x-2">
              {/* Favorite Button - Only show for connected partners */}
              {showFavoriteButton && onToggleFavorite && !isOwnProfile && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFavorite();
                  }}
                  className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
                  title={partner?.isFavorite ? "Remove from favorites" : "Add to favorites"}
                  aria-label={partner?.isFavorite ? "Remove from favorites" : "Add to favorites"}
                >
                  <Star
                    className={`w-5 h-5 ${
                      partner?.isFavorite
                        ? "fill-yellow-300 text-yellow-300"
                        : "text-white"
                    }`}
                  />
                </button>
              )}

              {/* Hamburger Menu - Only show for partners (not own profile) */}
              {showMenuButton && !isOwnProfile && partner && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
                      aria-label="More options"
                    >
                      <MoreVertical className="w-5 h-5 text-white" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {/* Remove Partner option - only show for connected partners */}
                    {isConnected && !isBlocked && (
                      <DropdownMenuItem
                        onClick={handleRemovePartner}
                        className="text-orange-600 focus:text-orange-600"
                      >
                        <UserMinus className="w-4 h-4 mr-2" />
                        Remove Partner
                      </DropdownMenuItem>
                    )}
                    {/* Report — App Store Guideline 1.2 requires reporting alongside blocking.
                        Shown even for a user who is already blocked: blocking stops them reaching
                        you, it does not tell anyone what they did. */}
                    <DropdownMenuItem
                      onClick={() => setReportDialogOpen(true)}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Flag className="w-4 h-4 mr-2" />
                      Report User
                    </DropdownMenuItem>
                    {isBlocked ? (
                      <DropdownMenuItem
                        onClick={handleUnblockUser}
                        className="text-green-600 focus:text-green-600"
                      >
                        <ShieldOff className="w-4 h-4 mr-2" />
                        Unblock User
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onClick={handleBlockUser}
                        className="text-red-600 focus:text-red-600"
                      >
                        <Ban className="w-4 h-4 mr-2" />
                        Block User
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Close Button */}
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
                aria-label="Close modal"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Avatar and Name */}
            <div className="flex flex-col items-center text-center">
              <Avatar className="w-24 h-24 ring-4 ring-white shadow-xl mb-4">
                <AvatarImage
                  src={user?.profileImageUrl || undefined}
                  alt={userDisplayName}
                />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-2xl font-bold">
                  {userInitials}
                </AvatarFallback>
              </Avatar>

              <DialogTitle className="text-2xl font-bold text-white mb-1">
                {userDisplayName}
              </DialogTitle>

              {user?.company && (
                <div className="flex items-center gap-1.5 text-white/90">
                  <Briefcase className="w-4 h-4" />
                  <span className="text-sm font-medium">{user.company}</span>
                </div>
              )}
            </div>

            {/* Member Since - Bottom Right */}
            {user?.createdAt && (
              <div className="mt-4 flex justify-end">
                <div className="flex items-center gap-2 text-white/90 text-xs">
                  <Calendar className="w-4 h-4" />
                  <span className="font-medium">
                    Member Since {new Date(user.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short'
                    })}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6 flex-1 overflow-y-auto">
            {/* Professional Information - FIRST */}
            {(() => {
              // Prefer the hook-fetched primary trade name (authoritative
              // trigger-maintained source). Fall back to whatever the caller
              // passed in only if the hook hasn't loaded yet.
              const primaryTradeName =
                fetchedPrimaryTradeName ?? user?.trigPrimaryTradeName ?? null;
              const hasAdditionalTrades = !!(userTrades && userTrades.length > 0);
              const showProfessionalInfo =
                !!primaryTradeName || !!user?.location || hasAdditionalTrades;
              if (!showProfessionalInfo) return null;
              return (
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                  Professional Info
                </h3>

                {primaryTradeName && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Primary Trade</p>
                      <p className="text-sm text-gray-900 font-medium">{primaryTradeName}</p>
                    </div>
                  </div>
                )}

                {/* Additional Trades - from user_profile_trades junction table */}
                {userTrades && userTrades.length > 0 && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                      <Wrench className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Additional Trades</p>
                      <p className="text-sm text-gray-900 font-medium">
                        {userTrades.map((trade) => trade.name).join(', ')}
                      </p>
                    </div>
                  </div>
                )}

                {user?.location && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Location</p>
                      <p className="text-sm text-gray-900 font-medium">{user.location}</p>
                    </div>
                  </div>
                )}
              </div>
              );
            })()}

            {/* Contact Information - SECOND - Show for connected users, respecting privacy settings */}
            {isConnected && (
              (user?.phone && user?.showPhoneToConnections === true) ||
              (user?.email && user?.showEmailToConnections === true)
            ) && (
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                  Contact Information
                </h3>

                {/* Phone - Show only if user allows phone visibility */}
                {user?.phone && user?.showPhoneToConnections === true && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Phone</p>
                      <a
                        href={`tel:${user.phone}`}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline"
                      >
                        {formatPhone(user.phone)}
                      </a>
                    </div>
                  </div>
                )}

                {/* Email - Show only if user allows email visibility */}
                {user?.email && user?.showEmailToConnections === true && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Email</p>
                      <a
                        href={`mailto:${user.email}`}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline break-all"
                      >
                        {user.email}
                      </a>
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* Company Information - THIRD */}
            {(() => {
              const hasCompanyInfo = user?.company || user?.companyLogoUrl || user?.companyBio;
              if (!hasCompanyInfo) return null;

              return (
                <div className="space-y-3">
                  <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                    Company Information
                  </h3>

                  <div className="flex items-start gap-4">
                    {/* Left column: Company Logo or Building icon fallback */}
                    {user?.companyLogoUrl ? (
                      <div className="w-16 h-16 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        <img
                          src={user.companyLogoUrl}
                          alt={`${user.company || 'Company'} logo`}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-7 h-7 text-blue-600" />
                      </div>
                    )}

                    {/* Right column: Company Name and Bio */}
                    <div className="flex-1 min-w-0">
                      {user?.company && (
                        <p className="text-sm text-gray-900 font-semibold">{user.company}</p>
                      )}
                      {user?.companyBio && (
                        <p className="text-sm text-gray-600 leading-relaxed mt-1">
                          {user.companyBio}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Personal Bio - FOURTH */}
            {(user?.bio || user?.personalBio) && (
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                  About
                </h3>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {user.bio || user.personalBio}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Service Areas - FIFTH */}
            {user?.serviceAreas && Array.isArray(user.serviceAreas) && user.serviceAreas.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-600" />
                  Service Areas
                </h3>
                <div className="flex flex-wrap gap-2">
                  {user.serviceAreas.map((area: string, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 text-xs font-medium rounded-full border border-purple-300/50"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Market Types - SIXTH */}
            {user?.marketTypes && Array.isArray(user.marketTypes) && user.marketTypes.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                  Market Types
                </h3>
                <div className="flex flex-wrap gap-2">
                  {user.marketTypes.map((market: string, index: number) => (
                    <Badge
                      key={index}
                      className="px-3 py-1.5 bg-gradient-to-r from-green-100 to-green-200 text-green-800 text-xs font-medium border border-green-300/50 hover:from-green-200 hover:to-green-300"
                    >
                      {market}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Relationship Summary - Only show for connected partners */}
            {!isOwnProfile && isConnected && (
              <div className="space-y-2">
                <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                  Relationship Summary
                </h3>

                <div className="grid grid-cols-2 gap-2">
                  {/* Referrals Sent */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-2.5 border border-blue-200 text-center">
                    <p className="text-xs font-semibold text-blue-900 mb-1">Referrals Sent</p>
                    <p className="text-2xl font-bold text-blue-900">{relationshipMetrics.referralsSent}</p>
                  </div>

                  {/* Referrals Received */}
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-2.5 border border-green-200 text-center">
                    <p className="text-xs font-semibold text-green-900 mb-1">Referrals Received</p>
                    <p className="text-2xl font-bold text-green-900">{relationshipMetrics.referralsReceived}</p>
                  </div>

                  {/* Referrals Completed */}
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-2.5 border border-purple-200 text-center">
                    <p className="text-xs font-semibold text-purple-900 mb-1">Referrals Completed</p>
                    <p className="text-2xl font-bold text-purple-900">{relationshipMetrics.referralsCompleted}</p>
                  </div>

                  {/* Revenue Generated */}
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-2.5 border border-amber-200 text-center">
                    <p className="text-xs font-semibold text-amber-900 mb-1">Revenue</p>
                    <p className="text-2xl font-bold text-amber-900">
                      ${relationshipMetrics.revenueGenerated.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Referral History - Only show for connected partners */}
            {!isOwnProfile && isConnected && (() => {
              // Combine sent and received referrals
              const allReferrals = [
                ...(sentReferrals || []),
                ...(receivedReferrals || [])
              ];

              // Sort by createdAt descending and take first 3
              const recentReferrals = allReferrals
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .slice(0, 3);

              if (recentReferrals.length === 0) return null;

              return (
                <div className="space-y-2">
                  <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                    Recent Referrals
                  </h3>

                  <div className="space-y-2">
                    {recentReferrals.map((referral) => (
                      <CompactReferralCard
                        key={referral.id}
                        referral={referral}
                      />
                    ))}
                  </div>

                  {/* View All Button */}
                  {allReferrals.length > 3 && (
                    <button
                      onClick={() => setViewAllReferralsOpen(true)}
                      className="w-full mt-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      View All ({allReferrals.length} Referrals)
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Empty State - Only show for own profile */}
            {isOwnProfile &&
             !fetchedPrimaryTradeName &&
             !user?.trigPrimaryTradeName &&
             !user?.location &&
             !(userTrades && userTrades.length > 0) &&
             !(Array.isArray(user?.marketTypes) && user.marketTypes.length > 0) && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 text-sm">
                  Complete your profile to help others understand your business better
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            {/* Action Buttons - Only show for connected partners */}
            {showActionButtons && !isOwnProfile && (
              <div className="flex gap-3 mb-4">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSendReferral?.();
                  }}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Referral
                </Button>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSendMessage?.();
                  }}
                  variant="outline"
                  className="flex-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </div>
            )}
            {/* Request Connection - Only for users who are not partners yet. The footer sits
                outside the scrolling content, so the button stays visible on long profiles. */}
            {!isOwnProfile && !isConnected && !isBlocked && (isRequestPending || onRequestConnection) && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onRequestConnection?.();
                }}
                disabled={isRequestPending}
                className="w-full mb-4 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white disabled:opacity-60"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {isRequestPending ? "Request Pending" : "Request Connection"}
              </Button>
            )}
            <p className="text-xs text-gray-500 text-center">
              {isOwnProfile
                ? "This is how your profile appears to other users"
                : "Public profile information"}
            </p>
          </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      {/* Block Confirmation Dialog */}
      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Block {userDisplayName}?</DialogTitle>
            <DialogDescription>
              Are you sure you want to block this user? They will no longer be able to see you in partner suggestions or send you connection requests.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>Note:</strong> Blocking is unidirectional - you will still be able to see them in your searches, but they won't see you.
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setBlockDialogOpen(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmBlockUser}
              disabled={blockUser.isLoading}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
            >
              <Ban className="w-4 h-4 mr-2" />
              {blockUser.isLoading ? "Blocking..." : "Block User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unblock Confirmation Dialog */}
      <Dialog open={unblockDialogOpen} onOpenChange={setUnblockDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-green-600">Unblock {userDisplayName}?</DialogTitle>
            <DialogDescription>
              Are you sure you want to unblock this user? They will be able to see you in partner suggestions and send you connection requests again.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                <strong>Note:</strong> After unblocking, this user will regain normal visibility of your profile.
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setUnblockDialogOpen(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmUnblockUser}
              disabled={unblockUser.isLoading}
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
            >
              <ShieldOff className="w-4 h-4 mr-2" />
              {unblockUser.isLoading ? "Unblocking..." : "Unblock User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View All Referrals Dialog */}
      <Dialog open={viewAllReferralsOpen} onOpenChange={setViewAllReferralsOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              All Referrals with {userDisplayName}
            </DialogTitle>
            <DialogDescription>
              Complete history of referrals shared between you and this partner
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto max-h-[60vh] pr-2">
            {(() => {
              // Combine sent and received referrals
              const allReferrals = [
                ...(sentReferrals || []),
                ...(receivedReferrals || [])
              ];

              // Sort by createdAt descending
              const sortedReferrals = allReferrals
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

              if (sortedReferrals.length === 0) {
                return (
                  <div className="text-center py-8 text-gray-500">
                    No referrals shared with this partner yet.
                  </div>
                );
              }

              return (
                <div className="space-y-3">
                  {sortedReferrals.map((referral) => (
                    <CompactReferralCard
                      key={referral.id}
                      referral={referral}
                    />
                  ))}
                </div>
              );
            })()}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setViewAllReferralsOpen(false)}
              className="w-full sm:w-auto"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Partner Confirmation Dialog - custom darker overlay (bg-black/90)
          to stand out over the profile modal. Portal mount order handles stacking. */}
      <Dialog open={removePartnerDialogOpen} onOpenChange={setRemovePartnerDialogOpen}>
        <DialogPortal>
          <DialogOverlay className="bg-black/90" />
          <DialogPrimitive.Content
            className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-md translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-[15px]"
          >
            <DialogHeader className="pr-8">
              <DialogTitle className="text-orange-600">Remove Partner</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove <span className="font-semibold text-foreground">{userDisplayName}</span> from your network? You will no longer be able to send referrals to each other until you reconnect.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-800">
                  <strong>Note:</strong> This action will remove the connection between you and this partner. Your referral history will be preserved, but you won't be able to send new referrals until you reconnect.
                </p>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setRemovePartnerDialogOpen(false)}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmRemovePartner}
                disabled={deleteConnection.isPending}
                className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white"
              >
                <UserMinus className="w-4 h-4 mr-2" />
                {deleteConnection.isPending ? "Removing..." : "Remove Partner"}
              </Button>
            </DialogFooter>
            {/* Close button */}
            <DialogPrimitive.Close className="absolute right-4 top-4 w-8 h-8 flex items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      {/* Report dialog. `canBlock` is off when they are already blocked — the checkbox would be a
          no-op — and off for a profile the user is not connected to, where there is no
          relationship to sever. */}
      {partner && (
        <ReportContentModal
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          reportedUserId={partner.id}
          reportedUserName={userDisplayName}
          contentType="profile"
          canBlock={!isBlocked}
        />
      )}
    </>
  );
}
